/**
 * @fileoverview Participating-company search, cards, and add action.
 * @module pages/b2bEventForm/B2BEventParticipantsSection
 */

import { useCallback } from "react";
import SearchBar from "../../components/SearchBar/index.jsx";
import { ReactComponent as AddCompanyIcon } from "../../assets/icons/B2BEvents/AddCompanyIcon.svg";
import { ReactComponent as MoreIcon } from "../../assets/icons/Chat/MoreIcon.svg";
import { ReactComponent as ActiveCompanyIcon } from "../../assets/icons/CompanyCatalogPage/ActiveCompanyIcon.svg";
import { ReactComponent as InactiveCompanyIcon } from "../../assets/icons/CompanyCatalogPage/InactiveCompanyIcon.svg";
import styles from "../../styles/B2BEventForm.module.css";

/**
 * @param {Object} participant
 * @returns {number}
 */
const uniqueExecutiveCount = (participant) =>
  new Set(
    (participant.ejecutivosCompradores ?? []).map((executive) => executive._id).filter(Boolean),
  ).size;

/**
 * @param {Object} props
 * @returns {JSX.Element}
 */
const ParticipantCard = ({
  participant,
  index,
  t,
  showDropdown,
  dropdownRef,
  onToggleDropdown,
  onEdit,
  onDelete,
}) => {
  const openMenu = useCallback(
    (event) => onToggleDropdown(event, index),
    [index, onToggleDropdown],
  );
  const editCompany = useCallback(
    (event) => onEdit(event, participant),
    [onEdit, participant],
  );
  const deleteCompany = useCallback(
    (event) => onDelete(event, participant),
    [onDelete, participant],
  );

  return (
    <div
      className={styles.participantCard}
      ref={showDropdown === index ? dropdownRef : null}
    >
      <div className={styles.participantHeader}>
        <div className={styles.participantNameRow}>
          {participant.activo === false ? (
            <InactiveCompanyIcon className={styles.participantStatusIcon} />
          ) : (
            <ActiveCompanyIcon className={styles.participantStatusIcon} />
          )}
          <span className={styles.participantName}>{participant.nombreComercial}</span>
        </div>
        <button type="button" className={styles.moreButton} onClick={openMenu}>
          <MoreIcon />
        </button>
        {showDropdown === index && (
          <div className={styles.chatHeaderDropdownMenu}>
            <button
              type="button"
              className={styles.chatHeaderDropdownItem}
              onClick={editCompany}
            >
              {t("pages.b2bEventForm.edit")}
            </button>
            <div className={styles.chatHeaderDropdownDivider} />
            <button
              type="button"
              className={`${styles.chatHeaderDropdownItem} ${styles.rejectItem}`}
              onClick={deleteCompany}
            >
              {t("pages.b2bEventForm.delete")}
            </button>
          </div>
        )}
      </div>
      <div className={styles.participantDetails}>
        <span className={styles.detailLabel}>
          {uniqueExecutiveCount(participant)} {t("pages.b2bEventForm.executives")}
        </span>
      </div>
    </div>
  );
};

/**
 * Empty list, empty search, or the company cards. One branch, then the next.
 * @param {Object} props
 * @returns {JSX.Element|JSX.Element[]}
 */
const ParticipantCards = ({
  t,
  participatingCompanies,
  filteredParticipatingCompanies,
  showDropdown,
  dropdownRef,
  onToggleDropdown,
  onEdit,
  onDelete,
}) => {
  if (participatingCompanies?.length === 0) {
    return (
      <div className={styles.noResults}>
        <p>{t("pages.b2bEventForm.noCompanies")}</p>
      </div>
    );
  }
  if (filteredParticipatingCompanies.length === 0) {
    return (
      <div className={styles.noResults}>
        <p>{t("pages.b2bEventForm.noCompanyFoundForSearch")}</p>
      </div>
    );
  }
  return filteredParticipatingCompanies.map((participant, index) => (
    <ParticipantCard
      key={participant.idEmpresaCompradora || index}
      participant={participant}
      index={index}
      t={t}
      showDropdown={showDropdown}
      dropdownRef={dropdownRef}
      onToggleDropdown={onToggleDropdown}
      onEdit={onEdit}
      onDelete={onDelete}
    />
  ));
};

/**
 * @param {Object} props
 * @returns {JSX.Element}
 */
const B2BEventParticipantsSection = ({
  t,
  eventData,
  participatingCompanies,
  searchParticipating,
  searchBarWidth,
  filteredParticipatingCompanies,
  participantError,
  showDropdown,
  dropdownRef,
  onSearch,
  onAdd,
  onToggleDropdown,
  onEdit,
  onDelete,
}) => (
  <div className={styles.formGroupTwo}>
    <div className={styles.sectionTitle}>
      <span>{t("pages.b2bEventForm.participatingCompanies")}</span>
    </div>
    {!eventData ? (
      <p className={styles.publishEventMessage}>
        {t("pages.b2bEventForm.publishEventToSeeCompanies")}
      </p>
    ) : (
      <div className={styles.companiesSubsection}>
        <div className={styles.participantsActions}>
          <div className={styles.searchBarWrapper}>
            <SearchBar
              placeholder={t("pages.b2bEventForm.searchCompanyPlaceholder")}
              value={searchParticipating}
              onChange={onSearch}
              width={searchBarWidth}
            />
          </div>
          <button
            type="button"
            className={styles.addCompanyButton}
            disabled={!eventData?._id}
            onClick={onAdd}
          >
            <AddCompanyIcon />
            {t("pages.b2bEventForm.addCompany")}
          </button>
        </div>
        <div className={styles.participantsListContainer}>
          <ParticipantCards
            t={t}
            participatingCompanies={participatingCompanies}
            filteredParticipatingCompanies={filteredParticipatingCompanies}
            showDropdown={showDropdown}
            dropdownRef={dropdownRef}
            onToggleDropdown={onToggleDropdown}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        </div>
        {participantError && <p className={styles.participantError}>{participantError}</p>}
      </div>
    )}
  </div>
);

export default B2BEventParticipantsSection;
